import { AxiosRequestConfig } from "axios";
import { httpService } from "./HttpServices";
import { ApiResponse } from "./UserService";

export interface HubSpotStatusData {
  connected: boolean;
  hubspotInstanceId?: string;
  expiresAt?: string;
  isExpired?: boolean;
  createdAt?: string;
}

export interface HubSpotCompany {
  id: string;
  name: string;
  domain?: string;
  industry?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface HubSpotContact {
  id: string;
  firstname?: string;
  lastname?: string;
  email: string;
  phone?: string | null;
  companyId?: string | null;
  createdAt: string;
  updatedAt: string;
  lifecyclestage?: string;
  ownerid?: string;
}

export interface HubSpotDeal {
  id: string;
  dealname: string;
  amount?: string;
  dealstage: string;
  pipeline: string;
  createdAt: string;
  updatedAt: string;
  closedate?: string;
}

export interface HubSpotPipelineStage {
  id: string;
  label: string;
  displayOrder: number;
  probability: number;
  isClosed: boolean;
}

export interface HubSpotPipeline {
  id: string;
  label: string;
  displayOrder: number;
  stages: HubSpotDealStage[];
  createdAt?: string;
  updatedAt?: string;
}

export interface HubSpotActivity {
  emailsLast7Days: number;
  callsLast7Days: number;
  meetingsBooked: number;
  tasksCompleted: number;
}

export interface HubSpotAlerts {
  missingFields: number;
  staleDeals: number;
  unassignedLeads: number;
}

export interface HubSpotDealInsights {
  total: number;
  active: number;
  atRisk: number;
  wonThisMonth: number;
}

export interface HubSpotCompanyInsights {
  total: number;
  active: number;
  newThisMonth: number;
}

export interface HubSpotPipelineInsights {
  total: number;
  healthyStages: number;
}

export interface HubSpotContactInsights {
  total: number;
  qualified: number;
}

export interface HubSpotInsights {
  deals: HubSpotDealInsights;
  companies: HubSpotCompanyInsights;
  pipelines: HubSpotPipelineInsights;
  contacts: HubSpotContactInsights;
  activity: HubSpotActivity;
  alerts: HubSpotAlerts;
}

export interface HubSpotData {
  insights: HubSpotInsights;
  aiSummary: string;
}

export interface HubSpotAuthorizationData {
  authorizationUrl: string;
}

export interface HubSpotDeploymentStatus {
  deployed: boolean;
  hubspotConnected: boolean;
  stripeConnected: boolean;
  hasActiveWorkflows: boolean;
  requiresStripe: boolean;
  missingConnections: string[];
}

export interface HubSpotDealStage {
  id: string;
  label: string;
  displayOrder: number;
  probability: string;
  closedWon: boolean;
  closedLost: boolean;
}

export interface WorkflowSuggestion {
  pipelineId: string;
  pipelineLabel: string;
  stageId: string;
  stageLabel: string;
  confidence: "high" | "medium" | "low";
  reasoning?: string;
}

export interface IntelligentSuggestions {
  closedWonContract: WorkflowSuggestion | null;
  closedWonSubscription: WorkflowSuggestion | null;
  renewalReminder: {
    sourcePipeline: WorkflowSuggestion | null;
    destinationPipeline: WorkflowSuggestion | null;
    destinationStage: WorkflowSuggestion | null;
  };
  provider: "openai" | "gemini" | "anthropic" | "fallback" | "none";
}

export interface ExistingContractObject {
  objectTypeId: string; // e.g., "2-12345678"
  name: string; // e.g., "contracts"
  labels: {
    singular: string; // e.g., "Contract"
    plural: string; // e.g., "Contracts"
  };
  fullyQualifiedName?: string;
}

export interface CheckExistingObjectResponse {
  exists: boolean;
  objectTypeId?: string;
  name?: string;
  labels?: {
    singular: string;
    plural: string;
  };
  fullyQualifiedName?: string;
}

export interface HubSpotIntegrationSettings {
  autoCreateSubscriptionOnClosedWon: boolean;
  // autoCreateContractOnClosedWon removed - contracts are automatic with subscriptions
  closedWonPipelineId?: string;
  closedWonStageIds?: string[];
  renewalReminders: {
    enabled: boolean;
    paused: boolean;
    daysBeforeExpiration: number;
    sourcePipelineId?: string;
    pipelineId: string;
    dealStage: string;
    dealOwnerId?: string;
  };
  subscriptionProductMapping?: {
    enabled: boolean;
    defaultProductId?: string;
    rules: any[];
  };
  syncContractsToHubSpot?: boolean;
  hubspotContractObjectType?: string;
  customObjectSetupStatus?: "not_started" | "in_progress" | "success" | "failed";
  customObjectSetupError?: string;
  customObjectSetupCompletedAt?: string;
  hubspotEnterpriseCapable?: boolean;
  uiExtensionEnabled?: boolean;
  usingExistingCustomObject?: boolean;
  existingCustomObjectName?: string;
}

export interface HubSpotSettingsWithSuggestions {
  settings: HubSpotIntegrationSettings;
  pipelines: HubSpotPipeline[];
  suggestions: IntelligentSuggestions | null;
  isFirstTimeSetup: boolean;
}

export class HubSpotService {
  async getHubSpotStatus(
    config?: AxiosRequestConfig
  ): Promise<{ data: HubSpotStatusData; message: string }> {
    try {
      const response = await httpService.get<ApiResponse<HubSpotStatusData>>(
        "/hubspot/status",

        config
      );
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to fetch HubSpot connection status.";
      throw new Error(errorMessage);
    }
  }

  async connectHubSpot(
    config?: AxiosRequestConfig
  ): Promise<{ data: HubSpotAuthorizationData; message: string }> {
    try {
      const response = await httpService.get<
        ApiResponse<HubSpotAuthorizationData>
      >("/hubspot/connect", config);
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to connect HubSpot.";
      throw new Error(errorMessage);
    }
  }

  async disconnectHubSpot(
    config?: AxiosRequestConfig
  ): Promise<{ data: {}; message: string }> {
    try {
      const response = await httpService.delete<ApiResponse<{}>>(
        "/hubspot/disconnect",

        config
      );
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to disconnect HubSpot.";
      throw new Error(errorMessage);
    }
  }

  async debugRefreshToken(
    config?: AxiosRequestConfig
  ): Promise<{ data: { accessToken: string }; message: string }> {
    try {
      const response = await httpService.get<
        ApiResponse<{ accessToken: string }>
      >("/hubspot/token/refresh", config);

      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to refresh HubSpot token.";
      throw new Error(errorMessage);
    }
  }

  async fetchHubSpotData(
    config?: AxiosRequestConfig
  ): Promise<{ data: HubSpotData; message: string }> {
    try {
      const response = await httpService.get<ApiResponse<HubSpotData>>(
        "/hubspot/fetch-all",

        config
      );
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message || "Failed to fetch HubSpot data.";
      throw new Error(errorMessage);
    }
  }

  async getHubSpotDeploymentStatus(
    config?: AxiosRequestConfig
  ): Promise<{ data: HubSpotDeploymentStatus; message: string }> {
    try {
      const response = await httpService.get<
        ApiResponse<HubSpotDeploymentStatus>
      >("/hubspot/deployment-status", config);
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to fetch HubSpot deployment status.";
      throw new Error(errorMessage);
    }
  }

  async getSettings(
    forceSuggestions: boolean = false,
    config?: AxiosRequestConfig
  ): Promise<{ data: HubSpotSettingsWithSuggestions; message: string }> {
    try {
      const params = forceSuggestions ? { forceSuggestions: "true" } : {};
      const response = await httpService.get<
        ApiResponse<HubSpotSettingsWithSuggestions>
      >("/hubspot/settings", {
        ...config,
        params,
      });
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to fetch HubSpot settings.";
      throw new Error(errorMessage);
    }
  }

  async getSettingsWithSuggestions(
    forceSuggestions: boolean = false,
    config?: AxiosRequestConfig
  ): Promise<{ data: HubSpotSettingsWithSuggestions; message: string }> {
    return this.getSettings(forceSuggestions, config);
  }

  /**
   * Check for existing contract custom object in HubSpot
   */
  async checkExistingContractObject(
    config?: AxiosRequestConfig
  ): Promise<{ data: CheckExistingObjectResponse; message: string }> {
    try {
      const response = await httpService.get<
        ApiResponse<CheckExistingObjectResponse>
      >("/hubspot/settings/check-existing-contract-object", config);
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to check for existing contract object.";
      throw new Error(errorMessage);
    }
  }

  async updateSettings(
    settings: Partial<HubSpotIntegrationSettings> & {
      useExistingCustomObject?: boolean; // Optional: set to true if user confirms using existing object
    },
    config?: AxiosRequestConfig
  ): Promise<{ data: HubSpotIntegrationSettings; message: string }> {
    try {
      const response = await httpService.put<
        ApiResponse<HubSpotIntegrationSettings>
      >("/hubspot/settings", settings, config);
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to update HubSpot settings.";
      throw new Error(errorMessage);
    }
  }

  async getProductMappingRules(
    config?: AxiosRequestConfig
  ): Promise<{ data: ProductMappingRules; message: string }> {
    try {
      const response = await httpService.get<ApiResponse<ProductMappingRules>>(
        "/hubspot/product-mapping-rules",
        config
      );
      return {
        data: response.data,
        message: response.message,
      };
    } catch (error: any) {
      const errorMessage =
        error?.response?.data?.message ||
        "Failed to fetch product mapping rules.";
      throw new Error(errorMessage);
    }
  }
}

export interface ProductMappingRules {
  enabled: boolean;
  defaultProductId?: string;
  defaultProductName?: string;
  rules: Array<any>;
  productCount?: number;
  autoEnabled?: boolean;
}

export const hubSpotService = new HubSpotService();
